import React, { useState, useCallback } from 'react';
import { Upload, File, X, FileText, Image } from 'lucide-react';
import { uploadFile } from '../../../utils/fileHelpers';
import { logError, logInfo } from '../../../utils/logger';
import { Toast } from '../../../components/common/Toast';

export const FileUploadModal = ({ isOpen, onClose, surveyId, onUpload }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({});
  const [toast, setToast] = useState(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    setProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');
    let successCount = 0;
    let failedFiles = [];

    try {
      // Upload files sequentially
      const uploadedUrls = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          setProgress(prev => ({ ...prev, [i]: 0 }));
          const url = await uploadFile(file, `surveys/${surveyId}/files`);
          uploadedUrls.push(url);
          setProgress(prev => ({ ...prev, [i]: 100 }));
          successCount++;
          logInfo(`File uploaded successfully: ${file.name}`);
        } catch (error) {
          logError(`Failed to upload ${file.name}:`, error);
          failedFiles.push(file.name);
          setToast({
            type: 'error',
            message: `Failed to upload ${file.name}${error.message ? `: ${error.message}` : ''}`
          });
          continue;
        }
      }

      if (uploadedUrls.length > 0) {
        logInfo('Files uploaded successfully');
        onUpload?.(uploadedUrls);
        onClose();
        setToast({
          type: successCount === files.length ? 'success' : 'warning',
          message: successCount === files.length
            ? `Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`
            : `Uploaded ${successCount} of ${files.length} files. Failed: ${failedFiles.join(', ')}`
        });
      }
    } catch (error) {
      logError('Failed to upload files:', error);
      setToast({
        type: 'error',
        message: error.message || 'Failed to upload files. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type.includes('pdf')) return FileText;
    return File;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#2a2a2a] rounded-lg w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Upload Files</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            disabled={uploading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Drop Zone */}
          <div 
            className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-300 mb-2">Drag and drop your files here or</p>
            <label className="cursor-pointer">
              <span className="text-blue-500 hover:text-blue-400">browse files</span>
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </label>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-300">Selected Files</h3>
              {files.map((file, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {React.createElement(getFileIcon(file), { 
                      className: "w-5 h-5 text-blue-400" 
                    })}
                    <div>
                      <div className="text-white text-sm">{file.name}</div>
                      <div className="text-gray-400 text-xs">
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                      {progress[index] !== undefined && (
                        <div className="w-full bg-gray-600 rounded-full h-1.5 mt-1">
                          <div 
                            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${progress[index]}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-gray-600 rounded"
                    disabled={uploading}
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={files.length === 0 || uploading}
              className={`bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            >
              {uploading ? 'Uploading...' : 'Upload Files'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};